import { Component, OnDestroy, OnInit } from '@angular/core';

import { Event, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { CommonUtil } from '../../core/Utils/common-util';
import { RootScopeDeclare } from '../../core/rootscope-declare';
import { FacilityService } from '../../core/services/facility.service';
import { RootScopeService } from '../../core/shared-service/root-scope.service';
import { GetSoftwarePendingCountService } from '../services/get-software-pending-count.service';

@Component({
  selector: 'app-select-facility',
  templateUrl: './select-facility.component.html',
  styleUrls: ['./select-facility.component.css'],
})
export class SelectFacilityComponent implements OnInit, OnDestroy {
  rootScopeData: RootScopeDeclare;
  isRtl: boolean;
  facilityListDropdownVisibility = false;
  currentRoute: string;
  selectedFacility: any;
  facilityList = [] as any[];
  facilityMap: Map<string, any>;
  isDisabledFlagOn = true;

  private sub = new Subscription();
  constructor(
    private rootScopeService: RootScopeService,
    private getSoftwarePendingCountService: GetSoftwarePendingCountService,
    private commonUtil: CommonUtil,
    private _facilityService: FacilityService,
    private router: Router,
    private loader: HaloLoaderService,
    private _amplitudeAnalytics: AmplitudeAnalyticsService
  ) {
    const getRootScopeSub = this.rootScopeService
      .getRootScope()
      .subscribe((rootScopeData) => (this.rootScopeData = rootScopeData));
    this.sub.add(getRootScopeSub);
  }

  ngOnInit() {
    const routeChangesSub = this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = this.router.url;
          if (
            this.currentRoute === '/videoLibrary' ||
            this.currentRoute === '/videoLibrary/customizeBranding/addLogo' ||
            this.currentRoute === '/videoLibrary/customizeBranding/banner' ||
            this.currentRoute === '/videoLibrary/customizeBranding/categoryDisplayName'
          ) {
            this.isDisabledFlagOn = false;
          } else {
            this.isDisabledFlagOn = true;
          }
          if (this.facilityList.length > 0) {
            for (let index = 0; index < this.facilityList.length; index++) {
              this.facilityList[index].disabled = this.isDisabled(this.facilityList[index].facilityId);
            }
          }
        }
      });
    this.sub.add(routeChangesSub);

    this.getAllFacilities();
    this.selectedFacility = this._facilityService.selectedFacility();

    // when facility change
    const facilityOnChangeSub = this._facilityService.selectedFacilitySource.subscribe(
      (res) => {
        this.selectedFacility = res;

        // if temp facility, redirect to edit facility page
        if (!res.summary?.facilityAddress) {
          this.router.navigate(['/facility/manage/edit']);
        } else if (res.summary.facilityRights.home != 'true') {
          if (
            !(
              window.location.hash === '#/subscription/portal_redirect' ||
              window.location.hash.indexOf('#/digital_coach/') > -1 ||
              window.location.hash === '#/facility/staff'
            ) ||
            res.summary.facilityRights.facilityStaff != 'true' ||
            !window.location.pathname.includes('/halo.html')
          ) {
            this.commonUtil.redirectToAvailablePage();
          }
        }
      },
      (err: any) => console.log(err)
    );
    this.sub.add(facilityOnChangeSub);

    const refreshFacilitiesSub = this._facilityService.refreshFacilities$.subscribe(() => {
      this.getAllFacilities();
    });
    this.sub.add(refreshFacilitiesSub);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  selectFacility(facilityId: string, disabledFacility: boolean) {
    this.rootScopeData.isFromFacilitySelection = facilityId;
    this.facilityListDropdownVisibility = false;
    if (this.rootScopeData.isPageHasUnsavedChanges) {
      this.rootScopeData.showUnSavedChangesModal = true;
    } else {
      this._facilityService.selectFacility(facilityId, true);
      this.rootScopeData.isFromFacilitySelection = '';
      this._amplitudeAnalytics.setUserProperty([{ key: 'Facility ID', value: facilityId }]);
    }
  }

  changeToRtl(): void {
    const selectedLang = this.rootScopeData.selectedLang;
    this.isRtl = false;
    if (selectedLang === 'ar' || selectedLang === 'he') {
      this.isRtl = true;
    }
  }

  getAllFacilities(): void {
    this.loader.startProcess();
    this._facilityService.get().then(
      (data) => {
        this.convertFacilities(data.facilities);
        this.rootScopeData.lastSelectedConsole = data.lastSelectedConsole;
        this.rootScopeData.showLFODTooltip = data.showLFODTooltip == 'false' ? false : true;
        this.rootScopeData.showLFODPremiumTooltip = data.showLFODPremiumTooltip == 'false' ? false : true;
        this.rootScopeData.showNewPermissionTooltip = data.showNewPermissionTooltip == 'false' ? false : true;
        this.rootScopeData.showVideoAddDescTooltip = data.showVideoAddDescTooltip == 'false' ? false : true;
        this.rootScopeData.showAttractScreenPopup = data.showAttractScreenPopup == 'false' ? false : true;
        this.rootScopeData.showFacilitySetupPopup = data.showFacilitySetupPopup == 'true';

        const popup: any = {};
        popup.showAquamanPerformanceRunPopup = data.showAquamanPerformanceRunPopup;
        popup.showAttractScreenPopup = data.showAttractScreenPopup;
        popup.showBluthPerformanceRunPopup = data.showBluthPerformanceRunPopup;
        popup.showCalendarFeaturePopup = data.showCalendarFeaturePopup;
        popup.showEntertainmentPopup = data.showEntertainmentPopup;
        popup.showHaloUpgradePopup = data.showHaloUpgradePopup;
        popup.showIdlePopStats = data.showIdlePopStats;
        popup.showNavigationPopup = data.showNavigationPopup;
        popup.showNewHomeDashPopup = data.showNewHomeDashPopup;
        popup.showOnDemandClassesPopup = data.showOnDemandClassesPopup;
        popup.showReleaseCenterPopup = data.showReleaseCenterPopup;
        popup.showServiceNoticesFeaturePopup = data.showServiceNoticesFeaturePopup;
        popup.showSupermanPerformanceRunPopup = data.showSupermanPerformanceRunPopup;
        popup.showSurveyPopup = data.showSurveyPopup;
        popup.showHalomediaPopup = data.showHalomediaPopup;
        popup.showHealthSafetyPopup = data.showHealthSafetyPopup;
        popup.fcAnnouncementBanner = data.fcAnnouncementBanner;
        popup.showMicrositePopup = data.showMicrositePopup;
        popup.showSubscriptionPopup = data.showSubscriptionPopup;
        popup.showOnboardingPopup = data.showOnboardingPopup;
        popup.showMultisitePopup = data.showMultisitePopup;
        popup.showVideoLibraryTooltip = data.showVideoLibraryTooltip;
        this.rootScopeData.popupData = popup;

        const mgrData: any = {};
        mgrData.hasOptedInFacility = data.hasOptedInFacility;
        mgrData.hasProEnabledFacility = data.hasProEnabledFacility;
        mgrData.isAdminAnyFacility = data.isAdminAnyFacility;
        mgrData.isOwnerAnyFacility = data.isOwnerAnyFacility;
        mgrData.hasCmsLegalReconsent = data.hasCmsLegalReconsent;

        if (data['isSubscriptionAccepted']) {
          mgrData.isSubscriptionAccepted = data['isSubscriptionAccepted'] == 'false' ? false : true;
        }
        if (data['ownerOptedFacilities']) {
          mgrData.ownerOptedFacilities = [...data['ownerOptedFacilities']];
        }

        this.rootScopeData.managerData = mgrData;

        this.changeToRtl();
        this.loader.endProcess();
        /**/
      },
      (error) => {
        this.loader.endProcess();
        console.error('API failed.');
      }
    );
  }

  convertFacilities(facilities: any): void {
    this.facilityMap = new Map(facilities.map((i: any) => [i.facilityId, i] as [string, any]));
    this.facilityList = facilities.map((x: any) => {
      const obj = {
        facilityName: x.facilityName,
        facilityId: x.facilityId,
        disabled: this.isDisabled(x.facilityId),
      };
      return obj;
    });
  }

  isDisabled(facilityId: string) {
    const facilityData = this.facilityMap.get(facilityId);
    if (
      this.currentRoute === '/enable_equipment' &&
      facilityData.summary.facilityRights.lfodEnabledEquipment === 'true' &&
      facilityData.summary.userRights.lfodEnabledEquipment === 'F'
    ) {
      return false;
    } else if (
      (this.currentRoute === '/videoLibrary' ||
        this.currentRoute === '/videoLibrary/customizeBranding/addLogo' ||
        this.currentRoute === '/videoLibrary/customizeBranding/banner' ||
        this.currentRoute === '/videoLibrary/customizeBranding/categoryDisplayName') &&
      facilityData.summary.facilityRights.lfodEnabledEquipment === 'true' &&
      facilityData.summary.userRights.lfodEnabledEquipment === 'F'
    ) {
      return !this._facilityService.isEligibleLFODPremiumConsole(facilityData);
    } else {
      return false;
    }
  }

  isEligibleLFOPremiumDConsole(facilityData: any) {
    if (facilityData.consoleEquipments) {
      const lfodConsoles = ['superman', 'mulder', 'hal'];
      const consoleTypes = facilityData.consoleEquipments.map((val: any) => val.consoleType);
      const isEligibleConsoleType = consoleTypes.some((t: any) => lfodConsoles.includes(t));
      if (isEligibleConsoleType) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }
}
